// Access requests (arc 101 slice 01) — the application that PRECEDES an
// invitation.
//
// Until this arc the waitlist form was an email plus a `console.info`: no row,
// no dedupe, and an SMTP failure lost the application (`overview.md` G1). An
// access request is the row. It is the operator's inbox — "who asked to get
// in" — and the invite action turns ONE of them into an `Invitation` plus a
// grant (`apps/api/src/deps/provision.ts`, seat C).
//
// A PLATFORM object, not a tenant one. An applicant has no account and usually
// no `users` row yet, so there is nothing to fence on; the transports that read
// or decide it sit behind the platform-admin scope (`admin:users`), the same
// gate `invitations` of kind `platform` sit behind.
//
// STATUS IS DERIVED, NEVER STORED — the `invitations` posture, for the same
// reason: `pending | invited | declined` is a pure function of two columns
// ({@link accessRequestStatus}), and a stored status would be a second answer
// that can disagree with the columns it was computed from.
//
// EMAIL IS THE ADDRESS. It is normalized through the SAME seam the invitation
// it becomes will use (`normalizeInvitationEmail`): the access request's
// address IS the invitation's address one click later, and two normalizations
// would be two spellings of one person. Nothing here treats the address as an
// identity; the invitation's acceptance binds the verified one.
//
// Pure types — no I/O.

import { z } from 'zod';
import { AccessRequestIdSchema } from '../value-objects/ids';
import { GRANT_AMOUNT_MAX } from './grant';
import { InvitationEmailSchema, normalizeInvitationEmail } from './invitation';

/**
 * Where the application came from. One value today: the marketing waitlist form
 * (`apps/marketing/src/routes/waitlist-submit.ts`). Text + a DB CHECK, not a
 * `pgEnum` (the D3 no-pgEnum stance), so the next source is a value here and a
 * constraint bump, not a type migration.
 */
export const AccessRequestSource = {
  Waitlist: 'waitlist',
} as const;
export const AccessRequestSourceSchema = z.enum(AccessRequestSource);
export type AccessRequestSource = z.infer<typeof AccessRequestSourceSchema>;

/**
 * Where an application stands, DERIVED from `(invitationId, decidedAt)`. Not a
 * column — see the module header.
 */
export const AccessRequestStatus = {
  /** Live: nobody has decided. The only state a re-submission folds into. */
  Pending: 'pending',
  /** Decided FOR: an invitation was minted for the address and bound here. */
  Invited: 'invited',
  /** Decided AGAINST: stamped, no invitation. Terminal; a fresh application is a new row. */
  Declined: 'declined',
} as const;
export const AccessRequestStatusSchema = z.enum(AccessRequestStatus);
export type AccessRequestStatus = z.infer<typeof AccessRequestStatusSchema>;

/** Bounds shared with the marketing form (`waitlist-submit.ts`): a form field, not a document. */
export const ACCESS_REQUEST_NAME_MAX = 100;
export const ACCESS_REQUEST_ORG_MAX = 200;
export const ACCESS_REQUEST_NOTE_MAX = 2000;

/** A trimmed optional free-text field: absent or blank collapses to null. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === undefined || v === null || v.length === 0 ? null : v));

/**
 * One access-request row.
 *
 * `invitationId` is a SOFT reference (`text`, no FK) on purpose: it is the audit
 * record of which invitation let this person in, and status is DERIVED from
 * `(invitationId, decidedAt)`. An FK that `set null`ed would therefore not merely
 * drop a pointer — it would flip `invited` into `declined`, and an operator
 * reading the list would see a person they let in as one they turned away.
 * The dangling id is an audit fact, the same posture as `grants.subject_id`.
 */
export const AccessRequestSchema = z
  .object({
    id: AccessRequestIdSchema,
    /**
     * Already normalized (`access_requests_email_normalized_ck`). REFUSED rather
     * than re-normalized: a row that reaches here unnormalized was written past
     * the CHECK, and quietly fixing it at read would hide that.
     */
    email: z
      .string()
      .max(320)
      .email()
      .refine((e) => e === normalizeInvitationEmail(e), 'a stored address is already normalized'),
    name: z.string().min(1).max(ACCESS_REQUEST_NAME_MAX),
    org: z.string().max(ACCESS_REQUEST_ORG_MAX).nullable(),
    note: z.string().max(ACCESS_REQUEST_NOTE_MAX).nullable(),
    source: AccessRequestSourceSchema,
    /** The `inv_` id minted for this application, or null. Soft reference — see above. */
    invitationId: z.string().nullable(),
    /** Who decided, either way. Null before a decision, and `set null` once that human is offboarded. */
    decidedByUserId: z.string().uuid().nullable(),
    createdAt: z.date(),
    /** Bumped by a re-submission of a live application — "they asked again". */
    updatedAt: z.date(),
    /** When it left `pending`, either way. The prune's age basis. */
    decidedAt: z.date().nullable(),
  })
  .superRefine((r, ctx) => {
    // A decision is ONE act: the stamp, the decider and (for an invite) the
    // invitation are written together. An invitation on an undecided row is a
    // person let in with no record of when; a decider on one is a decision
    // nobody can order.
    if (r.decidedAt === null && (r.invitationId !== null || r.decidedByUserId !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['decidedAt'],
        message: 'invitationId and decidedByUserId are written with decidedAt or not at all',
      });
    }
    if (r.updatedAt.getTime() < r.createdAt.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['updatedAt'],
        message: 'updatedAt cannot precede createdAt',
      });
    }
  });
export type AccessRequest = z.infer<typeof AccessRequestSchema>;

/**
 * The derived status. The invitation wins: an invited row is also a decided row
 * (`decidedAt` is stamped by both outcomes), so the order of the two tests is
 * what makes `invited` reachable at all.
 */
export function accessRequestStatus(
  r: Pick<AccessRequest, 'invitationId' | 'decidedAt'>,
): AccessRequestStatus {
  if (r.invitationId !== null) return AccessRequestStatus.Invited;
  if (r.decidedAt !== null) return AccessRequestStatus.Declined;
  return AccessRequestStatus.Pending;
}

/**
 * The POST body (`POST /v1/access-requests`, seat C) — what the marketing form
 * submits over the service token. The address goes through the invitation's
 * normalization seam so the row the operator later invites is spelled the way
 * the invitation index will look it up.
 */
export const AccessRequestCreateSchema = z.object({
  email: InvitationEmailSchema,
  name: z.string().trim().min(1).max(ACCESS_REQUEST_NAME_MAX),
  org: optionalText(ACCESS_REQUEST_ORG_MAX),
  note: optionalText(ACCESS_REQUEST_NOTE_MAX),
  source: AccessRequestSourceSchema,
});
export type AccessRequestCreate = z.infer<typeof AccessRequestCreateSchema>;

/**
 * THE DEFAULT INVITE BUNDLE — written here and nowhere else (`overview.md`
 * § 01, seat H's row). What one click on an application provisions for a
 * normal user let in for real-world testing: 5 AI devs on the person (a
 * user-subject `ai_devs` grant) and a 30-day invitation. `apps/api` defaults
 * to it through {@link AccessRequestInviteSchema}; the docs cite this constant.
 *
 * The applicant has no user row yet, so the grant cannot be issued at invite
 * time: it is BOOKED on the invitation (`Invitation.capacity`,
 * `InvitationCapacitySchema` — the same two fields) and issued on accept.
 *
 * No `boxes` and no `tier`, by owner ruling (2026-09-11): a user-subject grant
 * admits only `unlimited_fleet | concurrency | ai_devs`
 * (`GRANT_SUBJECT_KINDS[User]`, `grant.ts`, and the DB CHECK), and the box
 * budget is already DERIVED from `ai_devs`: `enrollmentBoxBudget` is
 * `aiDevs × SERVERS_PER_DEV + additiveTotal(grantedBoxes)`
 * (`packages/billing/src/tiers.ts`). Invited people are normal users on the
 * normal tier — the grant is the whole bundle.
 */
export const DEFAULT_INVITE_BUNDLE = {
  aiDevs: 5,
  expiresInDays: 30,
} as const satisfies { aiDevs: number; expiresInDays: number };

/**
 * A grant quantity the DB will accept: `grants_amount_ck` (migration 0188)
 * refuses `> GRANT_AMOUNT_MAX`, so the body refuses it first — a `2000` typed
 * for a `2` fails here with the field named, not three layers down as a 23514.
 */
const grantAmount = z.number().int().positive().max(GRANT_AMOUNT_MAX);

/**
 * How long an invited person's grant lasts, in days from the moment it is
 * issued; `null` is ONGOING (the grant carries no `expires_at`). One field shape
 * for every invite door: this body and `POST /v1/admin/provision` (plan 102
 * slice 05), stored as `InvitationCapacity.expiresInDays`.
 */
export const InviteExpiresInDaysSchema = z.number().int().positive().nullable();

/**
 * The admin invite body (`POST /v1/admin/access-requests/:id/invite`, seat C).
 * Every knob defaults to {@link DEFAULT_INVITE_BUNDLE}, so `{}` is the one-click
 * path and a non-default value is a deliberate departure the audit row records.
 */
export const AccessRequestInviteSchema = z.object({
  ai_devs: grantAmount.default(DEFAULT_INVITE_BUNDLE.aiDevs),
  expires_in_days: InviteExpiresInDaysSchema.default(DEFAULT_INVITE_BUNDLE.expiresInDays),
  reason: optionalText(500),
});
export type AccessRequestInvite = z.infer<typeof AccessRequestInviteSchema>;

/** The listing projection: the row plus its derived status, ISO-stamped for the wire. */
export type AccessRequestView = {
  id: string;
  email: string;
  name: string;
  org: string | null;
  note: string | null;
  source: AccessRequestSource;
  status: AccessRequestStatus;
  invitation_id: string | null;
  decided_by_user_id: string | null;
  created_at_iso: string;
  updated_at_iso: string;
  decided_at_iso: string | null;
};

/** Project one row for a listing surface. */
export function accessRequestView(r: AccessRequest): AccessRequestView {
  return {
    id: r.id,
    email: r.email,
    name: r.name,
    org: r.org,
    note: r.note,
    source: r.source,
    status: accessRequestStatus(r),
    invitation_id: r.invitationId,
    decided_by_user_id: r.decidedByUserId,
    created_at_iso: r.createdAt.toISOString(),
    updated_at_iso: r.updatedAt.toISOString(),
    decided_at_iso: r.decidedAt?.toISOString() ?? null,
  };
}
